import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import { listNegotiations, createNegotiation, listContractors } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, MessageSquare, ArrowRight } from 'lucide-react';

export default function Negotiations({ lang, tenantId }) {
  const [negotiations, setNegotiations] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ contractor_id: '', brief: '', title: '', channel: 'chat' });
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, [tenantId]);

  async function loadData() {
    setLoading(true);
    try {
      const [n, c] = await Promise.all([
        listNegotiations(tenantId),
        listContractors(tenantId)
      ]);
      setNegotiations(n);
      setContractors(c);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleCreate() {
    if (!form.contractor_id || !form.brief.trim()) {
      toast.error(lang === 'pl' ? 'Kontrahent i zlecenie s\u0105 wymagane' : 'Contractor and brief required');
      return;
    }
    try {
      const neg = await createNegotiation(form, tenantId);
      toast.success(lang === 'pl' ? 'Negocjacja utworzona' : 'Negotiation created');
      setDialogOpen(false);
      setForm({ contractor_id: '', brief: '', title: '', channel: 'chat' });
      navigate(`/negotiations/${neg.id}`);
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d' : 'Error');
    }
  }

  return (
    <div className="space-y-6" data-testid="negotiations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {t('negotiations', lang)}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === 'pl' ? `${negotiations.length} negocjacji` : `${negotiations.length} negotiations`}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="negotiation-create-button">
              <Plus className="w-4 h-4" />
              {t('newNegotiation', lang)}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('newNegotiation', lang)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs">{t('selectContractor', lang)} *</Label>
                <Select value={form.contractor_id} onValueChange={v => setForm(p => ({...p, contractor_id: v}))}>
                  <SelectTrigger className="mt-1" data-testid="negotiation-contractor-select">
                    <SelectValue placeholder={t('selectContractor', lang)} />
                  </SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('title', lang)}</Label>
                <Input
                  value={form.title}
                  onChange={e => setForm(p => ({...p, title: e.target.value}))}
                  placeholder={lang === 'pl' ? 'Opcjonalny tytu\u0142...' : 'Optional title...'}
                  className="mt-1"
                  data-testid="negotiation-title-input"
                />
              </div>
              <div>
                <Label className="text-xs">{t('brief', lang)} *</Label>
                <Textarea
                  value={form.brief}
                  onChange={e => setForm(p => ({...p, brief: e.target.value}))}
                  placeholder={lang === 'pl' ? 'Opisz zlecenie negocjacyjne...' : 'Describe negotiation brief...'}
                  className="mt-1 min-h-[100px]"
                  data-testid="negotiation-brief-input"
                />
              </div>
              <Button onClick={handleCreate} className="w-full" data-testid="negotiation-create-submit">
                {t('create', lang)}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : negotiations.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="text-center py-12">
            <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {lang === 'pl' ? 'Brak negocjacji. Utw\u00f3rz pierwsz\u0105!' : 'No negotiations. Create one!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {negotiations.map(neg => (
            <Card
              key={neg.id}
              className="border border-border hover:border-border/80 cursor-pointer transition-colors duration-150"
              onClick={() => navigate(`/negotiations/${neg.id}`)}
              data-testid={`negotiation-card-${neg.id}`}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium truncate">{neg.title}</h3>
                    <Badge variant={neg.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                      {neg.status === 'active' ? t('active', lang) : t('completed', lang)}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {neg.contractor_name} {neg.contractor_company ? `\u2022 ${neg.contractor_company}` : ''}
                    {' \u2022 '}{neg.message_count || 0} {lang === 'pl' ? 'wiadomo\u015bci' : 'messages'}
                    {' \u2022 '}{formatDate(neg.created_at)}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
